import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Clock,
  Pencil,
  Plus,
  Power,
  Trash2,
  Zap,
} from 'lucide-react';
import { Drawer } from '../../components/Drawer';
import { UserFilters } from '../../components/UserFilters';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Select,
  Skeleton,
  cn,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import { formatNumber, formatRelative } from '../../lib/format';
import { errorMessage, useToast } from '../../lib/toast';
import type {
  AppKey,
  AutomationRule,
  AutomationRuleInput,
  AutomationTrigger,
  QueueStats,
  TriggerCatalog,
  UserFilter,
} from '../../lib/types';
import { useTemplates } from './Templates';

/** Delay presets, so the common cases are one click rather than arithmetic. */
const DELAY_PRESETS: { label: string; minutes: number }[] = [
  { label: '1 min', minutes: 1 },
  { label: '10 min', minutes: 10 },
  { label: '1 hour', minutes: 60 },
  { label: '1 day', minutes: 1440 },
  { label: '3 days', minutes: 4320 },
];

function useTriggers() {
  return useQuery({
    queryKey: ['automation-triggers'],
    queryFn: () => api.get<TriggerCatalog>('/api/automation/triggers'),
    // A compiled-in registry; it cannot change without a deploy.
    staleTime: Infinity,
  });
}

export function AutomationsTab() {
  const rules = useQuery({
    queryKey: ['automation-rules'],
    queryFn: () => api.get<AutomationRule[]>('/api/automation/rules'),
  });
  const [editing, setEditing] = useState<AutomationRule | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-xl text-sm text-ink-2">
          A rule waits for something to happen, holds for a while, then checks
          the device again before sending. Conditions are re-read at send time,
          so “if they still haven’t subscribed” means exactly that.
        </p>
        <Button
          variant="primary"
          icon={<Plus size={16} />}
          onClick={() => setCreating(true)}
        >
          New rule
        </Button>
      </div>

      <QueuePanel />

      {rules.isError && <ErrorNote error={rules.error} />}

      {rules.isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-40" />
          ))}
        </div>
      ) : rules.data && rules.data.length > 0 ? (
        <ul className="grid gap-3 xl:grid-cols-2">
          {rules.data.map((rule) => (
            <li key={rule.id}>
              <RuleCard rule={rule} onEdit={() => setEditing(rule)} />
            </li>
          ))}
        </ul>
      ) : (
        <Card>
          <EmptyState
            icon={<Zap size={28} />}
            title="No automatic notifications yet"
            description="Create a rule to welcome new installs, or to follow up once someone links an account."
            action={
              <Button
                variant="primary"
                icon={<Plus size={16} />}
                onClick={() => setCreating(true)}
              >
                New rule
              </Button>
            }
          />
        </Card>
      )}

      {(creating || editing) && (
        <RuleEditor
          rule={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// ── Queue health ────────────────────────────────────────────────────────────

/**
 * What the queue is holding and how hard the process is working.
 *
 * The event-loop reading is here rather than buried in a log because it is the
 * thing that explains a delay: sends stand down while the API is busy, so a
 * backlog next to a high reading is the system behaving correctly, not a fault.
 */
function QueuePanel() {
  const queue = useQuery({
    queryKey: ['automation-queue'],
    queryFn: () => api.get<QueueStats>('/api/automation/queue'),
    refetchInterval: 15_000,
  });

  if (queue.isPending) return <Skeleton className="h-24" />;
  if (queue.isError) return <ErrorNote error={queue.error} />;
  if (!queue.data) return null;

  const { counts, next_due_at, overdue, load } = queue.data;
  const waiting = (counts.pending ?? 0) + (counts.processing ?? 0);
  const busy = load.heavy_sections > 0 || load.event_loop_p95_ms >= 120;

  return (
    <Card>
      <CardHeader
        title="Queue"
        description="Scheduled sends survive a redeploy — they live in the database, not in memory."
        action={
          <Badge tone={busy ? 'warn' : 'good'} icon={<Activity size={11} />}>
            {busy ? 'Yielding to traffic' : 'Idle'}
          </Badge>
        }
      />
      <dl className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-4 sm:p-5">
        <QueueStat label="Waiting" value={waiting} />
        <QueueStat label="Sent" value={counts.sent ?? 0} />
        <QueueStat label="Skipped" value={counts.skipped ?? 0} />
        <QueueStat
          label="Failed"
          value={counts.failed ?? 0}
          tone={(counts.failed ?? 0) > 0 ? 'danger' : 'neutral'}
        />
      </dl>
      <p className="border-t border-line px-4 py-2.5 text-xs text-ink-3 sm:px-5">
        {next_due_at
          ? `Next send ${formatRelative(next_due_at)}`
          : 'Nothing scheduled'}
        {overdue > 0 && ` · ${formatNumber(overdue)} past due`}
        {` · event loop p95 ${load.event_loop_p95_ms} ms`}
      </p>
    </Card>
  );
}

function QueueStat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'danger';
}) {
  return (
    <div className="rounded-lg bg-raised px-2.5 py-2">
      <dt className="text-[11px] text-ink-3">{label}</dt>
      <dd
        className={cn(
          'tnum mt-0.5 text-lg font-semibold',
          tone === 'danger' ? 'text-danger' : 'text-ink',
        )}
      >
        {formatNumber(value)}
      </dd>
    </div>
  );
}

// ── Rule card ───────────────────────────────────────────────────────────────

function RuleCard({
  rule,
  onEdit,
}: {
  rule: AutomationRule;
  onEdit: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const triggers = useTriggers();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['automation-rules'] });
    void queryClient.invalidateQueries({ queryKey: ['automation-queue'] });
  };

  const toggle = useMutation({
    mutationFn: () =>
      api.patch<AutomationRule>(`/api/automation/rules/${rule.id}`, {
        is_enabled: !rule.is_enabled,
      }),
    onSuccess: () => {
      toast.success(rule.is_enabled ? 'Rule paused' : 'Rule is live');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: () => api.delete<void>(`/api/automation/rules/${rule.id}`),
    onSuccess: () => {
      toast.success('Rule deleted');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const triggerLabel =
    triggers.data?.triggers.find((item) => item.trigger === rule.trigger)
      ?.label ?? rule.trigger;

  return (
    <Card className={cn('flex h-full flex-col', !rule.is_enabled && 'opacity-70')}>
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-ink">{rule.name}</h3>
            <Badge tone={rule.is_enabled ? 'good' : 'neutral'}>
              {rule.is_enabled ? 'Live' : 'Paused'}
            </Badge>
            <Badge>{rule.app_label}</Badge>
          </div>
          {rule.description && (
            <p className="mt-1 text-xs text-ink-2">{rule.description}</p>
          )}
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            variant="ghost"
            aria-label={rule.is_enabled ? 'Pause rule' : 'Enable rule'}
            loading={toggle.isPending}
            onClick={() => {
              if (
                !rule.is_enabled ||
                confirm(
                  `Pause “${rule.name}”? Anything it has queued and not yet sent will be cancelled.`,
                )
              ) {
                toggle.mutate();
              }
            }}
          >
            <Power
              size={15}
              className={rule.is_enabled ? 'text-good' : 'text-ink-3'}
            />
          </Button>
          <Button variant="ghost" onClick={onEdit} aria-label="Edit rule">
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            aria-label="Delete rule"
            loading={remove.isPending}
            onClick={() => {
              if (confirm(`Delete “${rule.name}”? This cannot be undone.`)) {
                remove.mutate();
              }
            }}
          >
            <Trash2 size={15} className="text-danger" />
          </Button>
        </div>
      </div>

      {/* The rule as a sentence — the form it was actually conceived in. */}
      <div className="mx-4 rounded-lg border border-line bg-raised px-3 py-2.5 text-sm text-ink">
        <span className="text-ink-3">When</span> {triggerLabel.toLowerCase()},{' '}
        <span className="text-ink-3">wait</span> {formatDelay(rule.delay_minutes)}
        {rule.conditions && Object.keys(rule.conditions).length > 0 && (
          <>
            , <span className="text-ink-3">and if</span>{' '}
            {describeConditions(rule.conditions)}
          </>
        )}
        , <span className="text-ink-3">send</span>{' '}
        <span className="font-medium">
          {rule.template_name ?? `template ${rule.template_id}`}
        </span>
        .
      </div>

      {rule.template_variables.length > 0 && (
        <p className="mx-4 mt-2 text-xs text-ink-3">
          Personalised with{' '}
          {rule.template_variables.map((variable) => `{{${variable}}}`).join(', ')}
        </p>
      )}

      <dl className="mt-3 grid grid-cols-4 gap-2 px-4 pb-3 text-xs">
        <MiniStat label="Waiting" value={rule.stats.pending} />
        <MiniStat label="Sent" value={rule.stats.sent} />
        <MiniStat label="Skipped" value={rule.stats.skipped} />
        <MiniStat label="Failed" value={rule.stats.failed} />
      </dl>

      <div className="mt-auto flex items-center gap-1.5 border-t border-line px-4 py-2.5 text-xs text-ink-3">
        <Clock size={13} />
        {rule.expires_after_minutes
          ? `Drops if it can't send within ${formatDelay(rule.expires_after_minutes)} of coming due`
          : 'Never expires'}
      </div>
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-raised px-2 py-1.5">
      <dt className="text-[11px] text-ink-3">{label}</dt>
      <dd className="tnum mt-0.5 font-medium text-ink">{formatNumber(value)}</dd>
    </div>
  );
}

// ── Editor ──────────────────────────────────────────────────────────────────

interface EditorState {
  name: string;
  description: string;
  trigger: AutomationTrigger;
  appKey: AppKey | '';
  templateId: number | '';
  delayMinutes: string;
  expiresAfterMinutes: string;
  conditions: UserFilter;
  priority: string;
}

function RuleEditor({
  rule,
  onClose,
}: {
  rule: AutomationRule | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const templates = useTemplates();
  const triggers = useTriggers();
  const isEdit = !!rule;

  const [state, setState] = useState<EditorState>(() => ({
    name: rule?.name ?? '',
    description: rule?.description ?? '',
    trigger: rule?.trigger ?? 'app_installed',
    appKey: rule?.app_key ?? '',
    templateId: rule?.template_id ?? '',
    delayMinutes: String(rule?.delay_minutes ?? 1),
    expiresAfterMinutes: rule?.expires_after_minutes
      ? String(rule.expires_after_minutes)
      : '',
    conditions: rule?.conditions ?? {},
    priority: String(rule?.priority ?? 100),
  }));

  const patch = (next: Partial<EditorState>) =>
    setState((current) => ({ ...current, ...next }));

  const template = templates.data?.find((item) => item.id === state.templateId);
  const definition = triggers.data?.triggers.find(
    (item) => item.trigger === state.trigger,
  );

  /**
   * Placeholders the chosen copy uses that this trigger cannot supply. Not an
   * error — every variable has a translated fallback — but worth saying, since
   * a "your account" stand-in is rarely what the operator pictured.
   */
  const unsupported = useMemo(() => {
    if (!template || !triggers.data) return [];
    const available = new Set([
      ...(definition?.variables ?? []),
      ...triggers.data.universal_variables,
    ]);
    const used = placeholdersIn(
      template.locales.flatMap((locale) => [locale.title, locale.body]),
    );
    return used.filter((variable) => !available.has(variable));
  }, [template, triggers.data, definition]);

  const save = useMutation({
    mutationFn: () => {
      const body: AutomationRuleInput = {
        name: state.name.trim(),
        description: state.description.trim() || undefined,
        template_id: Number(state.templateId),
        delay_minutes: Number(state.delayMinutes),
        expires_after_minutes: state.expiresAfterMinutes
          ? Number(state.expiresAfterMinutes)
          : undefined,
        app_key: state.appKey || undefined,
        conditions: compact(state.conditions),
        priority: Number(state.priority),
      };
      // The trigger is what a rule's queued jobs already describe, so it is
      // fixed once the rule exists — the backend rejects it on an update.
      if (!isEdit) body.trigger = state.trigger;

      return isEdit
        ? api.patch<AutomationRule>(`/api/automation/rules/${rule.id}`, body)
        : api.post<AutomationRule>('/api/automation/rules', body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Rule updated' : 'Rule created');
      void queryClient.invalidateQueries({ queryKey: ['automation-rules'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const complete =
    state.name.trim() &&
    state.templateId !== '' &&
    Number(state.delayMinutes) >= 0;

  return (
    <Drawer
      open
      title={isEdit ? `Edit “${rule.name}”` : 'New automation rule'}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!complete}
            onClick={() => save.mutate()}
          >
            {isEdit ? 'Save changes' : 'Create rule'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input
            value={state.name}
            onChange={(event) => patch({ name: event.target.value })}
            placeholder="Welcome new installs"
          />
        </Field>

        <Field label="Note" hint="Optional — why this rule exists">
          <Input
            value={state.description}
            onChange={(event) => patch({ description: event.target.value })}
          />
        </Field>

        {/* ── 1. When ──────────────────────────────────────────────────── */}
        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            When it fires
          </h3>

          <Field label="Trigger">
            <Select
              value={state.trigger}
              disabled={isEdit}
              onChange={(event) =>
                patch({ trigger: event.target.value as AutomationTrigger })
              }
            >
              {triggers.data?.triggers.map((item) => (
                <option key={item.trigger} value={item.trigger}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Field>
          {definition && (
            <p className="text-xs text-ink-3">{definition.description}</p>
          )}
          {isEdit && (
            <p className="text-xs text-ink-3">
              The trigger can’t be changed — jobs already queued describe the
              old one. Create a second rule instead.
            </p>
          )}

          <Field label="App">
            <Select
              value={state.appKey}
              onChange={(event) =>
                patch({ appKey: event.target.value as AppKey | '' })
              }
            >
              <option value="">All apps</option>
              <option value="dualchat">InvisChat</option>
              <option value="ig_saver">IG Saver</option>
              <option value="whatsi_saver">Whatsi Saver</option>
            </Select>
          </Field>
        </section>

        {/* ── 2. Wait ──────────────────────────────────────────────────── */}
        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            How long it waits
          </h3>

          <div className="flex flex-wrap gap-2">
            {DELAY_PRESETS.map((preset) => (
              <button
                key={preset.minutes}
                type="button"
                onClick={() => patch({ delayMinutes: String(preset.minutes) })}
                aria-pressed={Number(state.delayMinutes) === preset.minutes}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  Number(state.delayMinutes) === preset.minutes
                    ? 'border-brand bg-brand-soft text-brand'
                    : 'border-line text-ink-2 hover:border-line-strong hover:bg-raised',
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Delay (minutes)" hint="0 sends as soon as it fires">
              <Input
                type="number"
                min={0}
                value={state.delayMinutes}
                onChange={(event) =>
                  patch({ delayMinutes: event.target.value })
                }
              />
            </Field>
            <Field
              label="Expire after (minutes)"
              hint="Empty = never. Stops a backlog arriving stale."
            >
              <Input
                type="number"
                min={1}
                value={state.expiresAfterMinutes}
                onChange={(event) =>
                  patch({ expiresAfterMinutes: event.target.value })
                }
                placeholder="∞"
              />
            </Field>
          </div>
        </section>

        {/* ── 3. Conditions ────────────────────────────────────────────── */}
        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            Who still qualifies
          </h3>
          <p className="text-xs text-ink-3">
            Checked against the device when the wait is over, not when it fired.
            Leave empty to send to everyone the trigger caught.
          </p>
          <UserFilters
            value={state.conditions}
            onChange={(next) => patch({ conditions: next })}
          />
        </section>

        {/* ── 4. What ──────────────────────────────────────────────────── */}
        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            What it sends
          </h3>

          <Field label="Template">
            <Select
              value={state.templateId}
              onChange={(event) =>
                patch({
                  templateId: event.target.value
                    ? Number(event.target.value)
                    : '',
                })
              }
            >
              <option value="">Select a template…</option>
              {templates.data?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>

          {template && (
            <div className="rounded-lg border border-line bg-raised px-3 py-2.5">
              <p className="text-sm font-medium text-ink">
                {englishCopy(template.locales)?.title}
              </p>
              <p className="mt-0.5 text-xs text-ink-2">
                {englishCopy(template.locales)?.body}
              </p>
            </div>
          )}

          {definition && definition.variables.length > 0 && (
            <p className="text-xs text-ink-3">
              This trigger can fill in{' '}
              {definition.variables
                .map((variable) => `{{${variable}}}`)
                .join(', ')}
              , and {'{{app_name}}'} works anywhere.
            </p>
          )}

          {unsupported.length > 0 && (
            <p className="flex items-start gap-1.5 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
              <AlertTriangle size={14} className="mt-px shrink-0" />
              <span>
                This copy uses{' '}
                {unsupported.map((variable) => `{{${variable}}}`).join(', ')},
                which this trigger can’t supply. Devices will receive a generic
                stand-in instead of their own value.
              </span>
            </p>
          )}

          <Field
            label="Priority"
            hint="Lower goes first when several rules come due together"
          >
            <Input
              type="number"
              min={0}
              max={1000}
              value={state.priority}
              onChange={(event) => patch({ priority: event.target.value })}
            />
          </Field>
        </section>
      </div>
    </Drawer>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** `{{name}}`, matching the syntax the backend's renderer substitutes. */
const PLACEHOLDER = /\{\{\s*([a-z0-9_]{1,32})\s*\}\}/gi;

/** Every distinct placeholder used across [texts], lowercased. */
function placeholdersIn(texts: string[]): string[] {
  const found = new Set<string>();
  for (const text of texts) {
    for (const match of text.matchAll(PLACEHOLDER)) {
      const name = match[1];
      if (name) found.add(name.toLowerCase());
    }
  }
  return [...found];
}

function englishCopy(locales: { language: string; title: string; body: string }[]) {
  return locales.find((locale) => locale.language === 'en') ?? locales[0];
}

/** Minutes as the unit an operator would have said it in. */
export function formatDelay(minutes: number): string {
  if (minutes === 0) return 'no time at all';
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) {
    const hours = Math.round((minutes / 60) * 10) / 10;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const days = Math.round((minutes / 1440) * 10) / 10;
  return `${days} day${days === 1 ? '' : 's'}`;
}

/** The stored condition object, back as the phrase it was set from. */
function describeConditions(conditions: UserFilter): string {
  return Object.entries(conditions)
    .filter(([key]) => key !== 'app_key')
    .map(([key, value]) => {
      const label = key.replace(/^(is|has)_/, '').replace(/_/g, ' ');
      if (typeof value === 'boolean') {
        return value ? label : `not ${label}`;
      }
      return `${label} is ${String(value)}`;
    })
    .join(' and ');
}
